import { Body1, styled } from "@iventis/styles";
import React, { useEffect, useState } from "react";
import { useCadRotation } from "../../state/hooks/use-cad-rotation";
import { ControlContainer } from "./map-zoom";

export function RotationCad({
    placeRotationPivotPoint,
    placeRotationHandle,
    removePivotPoint,
    removeRotationHandle,
    pivotPoint,
    rotationHandle,
    placingPivotPoint,
    placingRotationHandle,
    cancelPlacePivotPoint,
    cancelPlaceRotationHandle,
}: {
    placeRotationPivotPoint: () => void;
    placeRotationHandle: () => void;
    removePivotPoint: () => void;
    removeRotationHandle: () => void;
    pivotPoint?: [number, number] | null;
    rotationHandle?: [number, number] | null;
    placingPivotPoint: boolean;
    placingRotationHandle: boolean;
    cancelPlacePivotPoint: () => void;
    cancelPlaceRotationHandle: () => void;
}) {
    const { rotation, setRotation } = useCadRotation();
    const [inputValue, setInputValue] = useState(rotation.toFixed(3));
    const formatRotation = (value: number) => value.toFixed(3);

    // Keep input in sync if rotation changes externally
    useEffect(() => {
        // Only update input if the rotation changed outside of typing
        setInputValue((prev) => {
            const asNumber = Number.parseFloat(prev);
            // If the input is not a number or doesn't match the external rotation, update
            if (Number.isNaN(asNumber) || asNumber !== rotation) {
                return formatRotation(rotation);
            }
            return prev;
        });
    }, [rotation]);

    const normalizeRotation = (val: number): number => {
        if (val > 360) return 0;
        if (val < 0) return 360;
        return val;
    };

    const handleInputChange = (valStr: string) => {
        setInputValue(valStr);
        const valNumber = Number.parseFloat(valStr);
        if (!Number.isNaN(valNumber)) {
            setRotation(normalizeRotation(valNumber));
        }
    };

    const handleInputBlur = () => {
        const valNumber = Number.parseFloat(inputValue);
        if (!Number.isNaN(valNumber)) {
            const normalized = normalizeRotation(valNumber);
            setInputValue(formatRotation(normalized));
            return;
        }
        setInputValue(formatRotation(rotation));
    };

    return (
        <ControlContainer>
            <Body1>Rotation</Body1>
            <input className="number-input" type="number" value={inputValue} onChange={(event) => handleInputChange(event.target.value)} onBlur={handleInputBlur} />
            <ButtonRow>
                <button type="button" title="Place Pivot Point" onClick={placeRotationPivotPoint}>
                    Place Pivot Point
                </button>
                {(pivotPoint || placingPivotPoint) && (
                    <RemovePointButton
                        type="button"
                        title={placingPivotPoint ? "Cancel Pivot Placement" : "Remove Pivot Point"}
                        onClick={placingPivotPoint ? cancelPlacePivotPoint : removePivotPoint}
                    >
                        X
                    </RemovePointButton>
                )}
            </ButtonRow>
            <ButtonRow>
                <button type="button" title="Place Rotation Handle" onClick={placeRotationHandle}>
                    Place Rotation Handle
                </button>
                {(rotationHandle || placingRotationHandle) && (
                    <RemovePointButton
                        type="button"
                        title={placingRotationHandle ? "Cancel Handle Placement" : "Remove Rotation Handle"}
                        onClick={placingRotationHandle ? cancelPlaceRotationHandle : removeRotationHandle}
                    >
                        X
                    </RemovePointButton>
                )}
            </ButtonRow>
        </ControlContainer>
    );
}

const ButtonRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const RemovePointButton = styled.button`
    width: 20px;
`;
